"use client";

/* eslint-disable no-restricted-properties */

import { useEffect } from "react";

declare global {
  interface Window {
    __msrNetworkTransferPatched?: boolean;
    __msrNetworkTransferCounts?: Record<string, number>;
  }
}

const TRANSFER_DEBUG_FLAG = "1";

const isEnabled = () =>
  process.env.NEXT_PUBLIC_NEON_TRANSFER_DEBUG === TRANSFER_DEBUG_FLAG;

const getByteLength = (value: string) => new TextEncoder().encode(value).byteLength;

const getRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const getRequestMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
};

const getRequestBytes = (input: RequestInfo | URL, init?: RequestInit) => {
  const body = init?.body;
  if (typeof body === "string") return getByteLength(body);
  if (body instanceof URLSearchParams) return getByteLength(body.toString());
  if (body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;

  if (typeof Request !== "undefined" && input instanceof Request) {
    const contentLength = input.headers.get("content-length");
    if (contentLength) {
      const parsed = Number.parseInt(contentLength, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
};

const getResponseBytes = async (response: Response) => {
  const contentLength = response.headers.get("content-length");
  if (contentLength) {
    const parsed = Number.parseInt(contentLength, 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  try {
    return (await response.clone().arrayBuffer()).byteLength;
  } catch {
    return null;
  }
};

export function NetworkObservabilityProvider() {
  useEffect(() => {
    if (!isEnabled()) return;
    if (typeof window === "undefined") return;
    if (window.__msrNetworkTransferPatched) return;

    const originalFetch = window.fetch.bind(window);
    window.__msrNetworkTransferCounts = window.__msrNetworkTransferCounts ?? {};
    window.__msrNetworkTransferPatched = true;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const urlString = getRequestUrl(input);
      const start = performance.now();
      const response = await originalFetch(input, init);

      try {
        const url = new URL(urlString, window.location.origin);
        if (!url.pathname.startsWith("/api/")) return response;

        const method = getRequestMethod(input, init);
        const routeKey = `${method} ${url.pathname}`;
        const count = (window.__msrNetworkTransferCounts?.[routeKey] ?? 0) + 1;
        window.__msrNetworkTransferCounts![routeKey] = count;

        const requestBytes = getRequestBytes(input, init);
        const responseBytes = await getResponseBytes(response);

        console.info("[neon-transfer] browser-fetch", {
          count,
          method,
          path: url.pathname,
          search: url.search,
          status: response.status,
          durationMs: Number((performance.now() - start).toFixed(1)),
          requestBytes,
          responseBytes,
          approxTransferBytes: (requestBytes ?? 0) + (responseBytes ?? 0) || null,
        });
      } catch {
        return response;
      }

      return response;
    };
  }, []);

  return null;
}
